import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'

import { useTranslation } from '../i18n'
import { onShowHelp } from './bus'
import { resolveHelpFile } from './resolveHelpFile'
import './help.css'

const helpFiles = import.meta.glob('./manual/*.md', { query: '?raw', import: 'default' })

type HelpLoader = () => Promise<string>

function renderMarkdown(markdown: string): ReactNode[] {
  const lines = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let paragraph: string[] = []
  let unordered: string[] = []
  let ordered: string[] = []
  let code: string[] = []
  let inCode = false

  const flushParagraph = () => {
    if (paragraph.length === 0) return
    blocks.push(
      <p key={`p-${blocks.length}`}>
        {paragraph.join(' ')}
      </p>,
    )
    paragraph = []
  }

  const flushUnordered = () => {
    if (unordered.length === 0) return
    blocks.push(
      <ul key={`ul-${blocks.length}`}>
        {unordered.map((item, index) => (
          <li key={`ul-item-${index}`}>{item}</li>
        ))}
      </ul>,
    )
    unordered = []
  }

  const flushOrdered = () => {
    if (ordered.length === 0) return
    blocks.push(
      <ol key={`ol-${blocks.length}`}>
        {ordered.map((item, index) => (
          <li key={`ol-item-${index}`}>{item}</li>
        ))}
      </ol>,
    )
    ordered = []
  }

  const flushCode = () => {
    if (code.length === 0) return
    blocks.push(
      <pre key={`pre-${blocks.length}`}>
        <code>{code.join('\n')}</code>
      </pre>,
    )
    code = []
  }

  const flushAll = () => {
    flushParagraph()
    flushUnordered()
    flushOrdered()
  }

  for (const rawLine of lines) {
    const trimmed = rawLine.trim()

    if (trimmed.startsWith('```')) {
      flushAll()
      if (inCode) {
        flushCode()
      }
      inCode = !inCode
      continue
    }

    if (inCode) {
      code.push(rawLine)
      continue
    }

    if (!trimmed) {
      flushAll()
      continue
    }

    if (trimmed === '---') {
      flushAll()
      blocks.push(<hr key={`hr-${blocks.length}`} />)
      continue
    }

    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/)
    if (headingMatch) {
      flushAll()
      const level = headingMatch[1].length
      const text = headingMatch[2]
      if (level === 1) blocks.push(<h1 key={`h1-${blocks.length}`}>{text}</h1>)
      else if (level === 2) blocks.push(<h2 key={`h2-${blocks.length}`}>{text}</h2>)
      else blocks.push(<h3 key={`h3-${blocks.length}`}>{text}</h3>)
      continue
    }

    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      flushParagraph()
      flushOrdered()
      unordered.push(trimmed.slice(2).trim())
      continue
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/)
    if (orderedMatch) {
      flushParagraph()
      flushUnordered()
      ordered.push(orderedMatch[1].trim())
      continue
    }

    flushUnordered()
    flushOrdered()
    paragraph.push(trimmed)
  }

  flushAll()
  flushCode()

  if (blocks.length === 0) {
    return [<p key="empty">{markdown}</p>]
  }

  return blocks.map((node, index) => <Fragment key={`fragment-${index}`}>{node}</Fragment>)
}

export function HelpModal() {
  const { t } = useTranslation()
  const [content, setContent] = useState('')
  const [open, setOpen] = useState(false)
  const [activeFile, setActiveFile] = useState('')

  const helpLoaders = useMemo(() => {
    const loaders = new Map<string, HelpLoader>()
    Object.entries(helpFiles).forEach(([path, loader]) => {
      const fileName = path.split('/').pop()
      if (!fileName) return
      loaders.set(fileName, loader as HelpLoader)
    })
    return loaders
  }, [])

  useEffect(() => {
    const openHelp = async (route: string) => {
      const requestedFile = resolveHelpFile(route)
      const selectedFile = helpLoaders.has(requestedFile) ? requestedFile : 'index.md'
      const loader = helpLoaders.get(selectedFile)
      if (!loader) {
        setContent('')
        setActiveFile('')
        setOpen(false)
        return
      }

      const markdown = await loader()
      setContent(markdown)
      setActiveFile(selectedFile)
      setOpen(true)
    }

    const customEventHandler = (event: Event) => {
      const route = (event as CustomEvent<string>).detail
      if (typeof route !== 'string') {
        return
      }

      void openHelp(route)
    }

    const off = onShowHelp(openHelp)
    window.addEventListener('sp-help:show', customEventHandler as EventListener)
    return () => {
      off()
      window.removeEventListener('sp-help:show', customEventHandler as EventListener)
    }
  }, [helpLoaders])

  useEffect(() => {
    if (!open) {
      return
    }

    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
      }
    }

    window.addEventListener('keydown', keyHandler)
    return () => window.removeEventListener('keydown', keyHandler)
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  const renderedContent = useMemo(() => renderMarkdown(content), [content])

  if (!open) {
    return null
  }

  return (
    <div
      className="sp-help-overlay"
      data-testid="sp-help-overlay"
      role="dialog"
      aria-modal="true"
      onClick={() => setOpen(false)}
    >
      <section className="sp-help-modal" data-testid="sp-help-modal" onClick={(event) => event.stopPropagation()}>
        <header className="sp-help-header">
          <code className="sp-help-route">{activeFile}</code>
          <button
            className="sp-help-close"
            data-testid="sp-help-close"
            type="button"
            aria-label={t('helpModal.closeAriaLabel')}
            title={t('helpModal.closeAriaLabel')}
            onClick={() => setOpen(false)}
          >
            x
          </button>
        </header>
        <article className="sp-help-content">{renderedContent}</article>
      </section>
    </div>
  )
}